// SentryFindingsPanel — the operator's view of SENTRY's continuous
// audit. Lists open + acknowledged findings sorted severity-first,
// each with a per-check "Fix" hint pulled from the detail payload.
//
// Actions: acknowledge (I saw it, working on it) or dismiss (not
// applicable to me). Auto-resolved findings drop off silently on
// the next tick.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

const SEVERITY_META = {
  critical: { color: 'red',     label: 'Critical' },
  warn:     { color: 'amber',   label: 'Warn' },
  info:     { color: 'sky',     label: 'Info' },
};

const CATEGORY_META = {
  system:     { emoji: '⚙️',  label: 'System health' },
  data:       { emoji: '🗄️',  label: 'Data integrity' },
  financial:  { emoji: '💰',  label: 'Financial' },
  ai:         { emoji: '🤖',  label: 'AI performance' },
  human:      { emoji: '👤',  label: 'Human activity' },
  compliance: { emoji: '⚖️',  label: 'Compliance' },
};

async function bearerHeaders() {
  const token = readPersistedJwtDirect();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function SentryFindingsPanel({ showToast = () => {} }) {
  const [state, setState] = React.useState('loading');
  const [findings, setFindings] = React.useState([]);
  const [summary, setSummary]   = React.useState({ critical: 0, warn: 0, info: 0, total: 0 });
  const [expanded, setExpanded] = React.useState({});
  const [busy, setBusy]         = React.useState(false);

  const load = React.useCallback(async () => {
    setState('loading');
    try {
      const r = await fetch('/api/audit/findings', { headers: await bearerHeaders() });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body?.ok) {
        setState('error');
        return;
      }
      setFindings(body.findings || []);
      setSummary(body.summary || { critical: 0, warn: 0, info: 0, total: 0 });
      setState('ready');
    } catch { setState('error'); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await fetch('/api/audit/action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await bearerHeaders()) },
        body: JSON.stringify({ action: 'run_now' }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body?.ok) {
        showToast(`Audit failed: ${body?.error || r.status}`, 'error');
        return;
      }
      const s = body.summary || {};
      showToast(`Audit complete — ${s.findings_new || 0} new, ${s.findings_resolved || 0} resolved.`);
      await load();
    } finally { setBusy(false); }
  };

  const act = async (id, action) => {
    const r = await fetch('/api/audit/action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await bearerHeaders()) },
      body: JSON.stringify({ action, id }),
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      showToast(`Couldn't ${action}: ${body?.error || r.status}`, 'error');
      return;
    }
    await load();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <span>🛡️</span> SENTRY — continuous audit
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Every 30 minutes SENTRY audits every data point across your CRM: your book, statements, phone numbers, appointments, AI activity, override math. Anything that drifts surfaces here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runNow}
            disabled={busy}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-bold px-3 py-1.5 rounded"
          >
            {busy ? 'Auditing…' : 'Run audit now'}
          </button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {['critical', 'warn', 'info'].map((sev) => {
          const meta = SEVERITY_META[sev];
          const n = summary[sev] || 0;
          return (
            <span
              key={sev}
              className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-${meta.color}-50 border border-${meta.color}-200 text-${meta.color}-800 ${n === 0 ? 'opacity-40' : ''}`}
            >
              {meta.label}: {n}
            </span>
          );
        })}
        {summary.total === 0 && (
          <span className="text-xs text-emerald-700 font-semibold">✓ Everything looks clean.</span>
        )}
      </div>

      {state === 'loading' && (
        <div className="text-sm text-slate-500">Loading findings…</div>
      )}

      {state === 'error' && (
        <div className="text-sm text-red-700">Couldn't load findings. Try again in a moment.</div>
      )}

      {state === 'ready' && findings.length === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-900">
          <div className="font-semibold">Nothing to flag right now.</div>
          <div className="text-xs mt-1 text-emerald-800">SENTRY will keep watching. If anything drifts, it'll show up here within 30 minutes.</div>
        </div>
      )}

      {state === 'ready' && findings.length > 0 && (
        <div className="space-y-2">
          {findings.map((f) => {
            const sev = SEVERITY_META[f.severity] || SEVERITY_META.info;
            const cat = CATEGORY_META[f.category] || { emoji: '🔎', label: f.category };
            const isOpen = expanded[f.id];
            return (
              <div key={f.id} className={`bg-${sev.color}-50 border border-${sev.color}-200 rounded-lg`}>
                <div
                  className="flex items-start gap-3 p-3 cursor-pointer"
                  onClick={() => setExpanded(x => ({ ...x, [f.id]: !x[f.id] }))}
                >
                  <div className="text-lg leading-none mt-0.5">{cat.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-${sev.color}-100 text-${sev.color}-800`}>
                        {sev.label}
                      </span>
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider">{cat.label}</span>
                      {f.status === 'acknowledged' && (
                        <span className="text-[10px] text-slate-500">· Acknowledged</span>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-slate-900 mt-0.5">{f.title}</div>
                    {f.detail?.fix && !isOpen && (
                      <div className="text-xs text-slate-600 mt-0.5 line-clamp-1">
                        <strong>Fix:</strong> {f.detail.fix}
                      </div>
                    )}
                  </div>
                  <div className="text-slate-400 text-xs">{isOpen ? '▾' : '▸'}</div>
                </div>

                {isOpen && (
                  <div className="border-t border-slate-200 px-3 py-3 space-y-2 text-xs text-slate-700">
                    {f.detail?.fix && (
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Fix</div>
                        <div className="mt-0.5">{f.detail.fix}</div>
                      </div>
                    )}
                    {Object.entries(f.detail || {})
                      .filter(([k]) => k !== 'fix')
                      .map(([k, v]) => (
                        <div key={k}>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{k.replace(/_/g, ' ')}</div>
                          <div className="mt-0.5 font-mono text-[11px] whitespace-pre-wrap break-words bg-white border border-slate-200 rounded px-2 py-1.5 max-h-40 overflow-y-auto">
                            {typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)}
                          </div>
                        </div>
                      ))}
                    <div className="flex items-center gap-2 pt-1">
                      {f.status !== 'acknowledged' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); act(f.id, 'acknowledge'); }}
                          className="text-xs bg-slate-900 hover:bg-slate-800 text-white font-semibold px-2.5 py-1 rounded"
                        >
                          Acknowledge
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); act(f.id, 'dismiss'); }}
                        className="text-xs text-slate-500 hover:text-slate-900 font-medium"
                      >
                        Dismiss
                      </button>
                      <span className="text-[10px] text-slate-400 ml-auto">
                        First seen {new Date(f.first_seen_at).toLocaleString()} · Last seen {new Date(f.last_seen_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
